import type { Component } from '../schema';
import type { SelectionsMap } from './selections';
import type { EvaluatedMap } from '../eval/types';
import type { ExternalValues } from '../eval/evaluator';
import { evaluateAll } from '../eval/evaluator';
import { evaluateRules } from '../rules/evaluator';
import type { RuleResult } from '../rules/types';
import { effectiveSelections } from './effectiveSelections';

export interface DerivedConfiguration {
    /** Rule-evaluation output — visibility, blacklists, predefines, errors. */
    rules: RuleResult;
    /**
     * Selections with rule-removed Characteristics and blacklisted Values
     * stripped. **This** is what every pricing, payload and image path has
     * to read; the store's raw map still holds the customer's original pick
     * so it returns when the rule stops matching.
     */
    selections: SelectionsMap;
    /** Calculated Characteristics (expression / combi / matrix / external). */
    evaluated: EvaluatedMap;
}

/**
 * The one place that knows how to get from (Component + raw selections) to
 * everything derived from them.
 *
 * A Characteristic a rule has hidden — or a Value a rule has blacklisted —
 * must not trigger other rules. Legacy guarantees that by destroying the pick
 * (`setActiveChoice(null)`, option/abstract.js:1158) and re-running the
 * blacklist rules until nothing changes (`processBlacklistingRules`). V3 keeps
 * the customer's raw pick in the store so it comes back when the rule stops
 * matching, and reaches the same result as a fixpoint:
 *
 *  1. `evaluateRules` against the raw selections.
 *  2. `effectiveSelections` strips what those rules hide or blacklist.
 *  3. `evaluateRules` again against the stripped map — a rule whose
 *     condition only held through a now-hidden pick stops matching.
 *  4. Repeat 2–3 until the hidden / blacklisted sets stop changing.
 *
 * A rule that hides the very Characteristic its condition reads would flip
 * between two states forever; such a cycle settles on the state that hides
 * the most, which is what the rule asks for.
 *
 * `evaluateAll` runs over each stripped map with the hidden set passed along —
 * calculated Characteristics have no selection to strip, so a rule-removed
 * formula only resolves to `null` if the evaluator is told about it.
 *
 * Every consumer calls this instead of repeating the sequence — the
 * duplication is exactly how hidden Characteristics kept their price in the
 * formula path while the UI had already dropped them.
 */
export function deriveConfiguration(args: {
    component: Component;
    selections: SelectionsMap;
    externalValues?: ExternalValues | undefined;
}): DerivedConfiguration {
    const { component, selections, externalValues } = args;
    // The React root and the instance API both derive from the same store
    // change with the same inputs. Remembering the last result per Component
    // turns the second call into a lookup instead of a full re-evaluation.
    const last = lastDerived.get(component);
    if (last && last.selections === selections && last.externalValues === externalValues) {
        return last.result;
    }
    // Rules may test calculated Characteristics (a formula, a matrix result),
    // which have no selection of their own, so each pass evaluates them first.
    const preRules = evaluateAll(component, selections, externalValues);
    let result = settle(component, selections, externalValues, evaluateRules(component, selections, preRules), preRules);

    // Nothing to strip (the usual case): the rules already saw what counts.
    // Otherwise iterate — every pass, even one that strips nothing again,
    // has to be checked, or a rule hiding its own condition would end on
    // the state that flips back on the next render.
    const seen: RuleResult[] = [result.rules];
    for (let pass = 1; result.selections !== selections || pass > 1; pass++) {
        if (pass >= MAX_RULE_PASSES) break;
        const next = evaluateRules(component, result.selections, result.evaluated);
        if (sameVisibility(next, result.rules)) {
            // Stable. Take this pass's rules: its errors and predefines were
            // also decided without the hidden picks.
            result = { ...result, rules: next };
            break;
        }
        const cycleStart = seen.findIndex((r) => sameVisibility(r, next));
        if (cycleStart >= 0) {
            const widest = seen.slice(cycleStart).reduce((a, b) => (visibilitySize(b) > visibilitySize(a) ? b : a));
            result = settle(component, selections, externalValues, widest, preRules);
            break;
        }
        seen.push(next);
        result = settle(component, selections, externalValues, next, preRules);
    }

    lastDerived.set(component, { selections, externalValues, result });
    return result;
}

/** More passes than any real rule chain needs; legacy chains settle in 2–4. */
const MAX_RULE_PASSES = 10;

/** Strip what `rules` remove from the raw selections and evaluate the rest. */
function settle(
    component: Component,
    selections: SelectionsMap,
    externalValues: ExternalValues | undefined,
    rules: RuleResult,
    preRules: EvaluatedMap,
): DerivedConfiguration {
    const effective = effectiveSelections(selections, rules);
    // Nothing hidden and nothing stripped: a second pass would see exactly
    // the inputs of the first one, so its result is the same.
    const evaluated = effective === selections && rules.hiddenCharacteristics.size === 0
        ? preRules
        : evaluateAll(component, effective, externalValues, rules.hiddenCharacteristics);
    return { rules, selections: effective, evaluated };
}

function sameVisibility(a: RuleResult, b: RuleResult): boolean {
    return sameSet(a.hiddenCharacteristics, b.hiddenCharacteristics)
        && sameSet(a.blacklistedValues, b.blacklistedValues);
}

function sameSet(a: ReadonlySet<number>, b: ReadonlySet<number>): boolean {
    if (a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
}

function visibilitySize(r: RuleResult): number {
    return r.hiddenCharacteristics.size + r.blacklistedValues.size;
}

const lastDerived = new WeakMap<Component, {
    selections: SelectionsMap;
    externalValues: ExternalValues | undefined;
    result: DerivedConfiguration;
}>();
